<script setup lang="ts">
import { onMounted, onUnmounted, ref, useHost } from 'vue';
import { getSdk } from '../../utils/sdk';

const emit = defineEmits(['drop']);

const host = useHost();

const isDropping = ref(false);
const hasClicked = ref(false);
const isWinner = ref(false);
const clawContainer = ref<HTMLElement | null>(null);
const prize = ref('');
const clawImage = ref('/files/449/uploads/e6fe6c0f-bd75-4822-95ee-c55622856dd6.png');
const machineFrame = ref('/files/449/uploads/ba54f483-501a-48ea-8c4f-1746242aa8c2.png');
const clawHolder = ref('/files/449/uploads/13d8ee5f-3ac2-4396-8530-10bfeedbd1ac.png');

const handleDrop = () => {
  if (isDropping.value || hasClicked.value) return;

  hasClicked.value = true;
  isDropping.value = true;
  emit('drop');

  if (clawContainer.value) {
    // Get the current transform value
    const computedStyle = window.getComputedStyle(clawContainer.value);
    const currentTransform = computedStyle.transform;

    // Stop the animation and maintain current position
    clawContainer.value.style.animation = 'none';
    clawContainer.value.style.transform = currentTransform;

    // Get the claw-prize wrapper element
    const wrapper = clawContainer.value.querySelector('.claw-prize-wrapper') as HTMLElement;
    const prize = clawContainer.value.querySelector('.prize') as HTMLElement;

    if (wrapper) {
      // Determine the drop distance based on screen size
      const dropDistance = window.innerWidth <= 768 ? '150px' : '200px';

      // Animate the wrapper down and up
      wrapper.animate(
        [{ transform: 'translateY(0)' }, { transform: `translateY(${dropDistance})` }, { transform: 'translateY(0)' }],
        {
          duration: 2000,
          easing: 'ease-in-out',
          fill: 'forwards'
        }
      );

      // Show prize during the animation
      setTimeout(() => {
        if (prize) {
          prize.style.opacity = '1';
        }
        clawImage.value = '/files/449/uploads/eedd4b4e-1048-4d6d-ba46-610c1a89f303.png';
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('prizeWon'));
        }, 500);
      }, 1000);

      // Reset state after animation
      setTimeout(() => {
        isDropping.value = false;
        if (host) {
          const evt = new CustomEvent('endgame', {
            bubbles: true,
            detail: {
              winner: isWinner.value
            }
          });
          host.dispatchEvent(evt);
        }
      }, 2000);
    }
  }
};

onMounted(async () => {
  document.body.classList.add('hide-gameplay');

  const sdk = await getSdk();

  // Get settings
  const { settings } = await sdk.customSolutions.get<{
    machineFrame: string;
    clawHolder: string;
  }>('louis-poulsen-campaign-claw-game');

  if (settings?.machineFrame) {
    machineFrame.value = settings.machineFrame;
  }
  if (settings?.clawHolder) {
    clawHolder.value = settings.clawHolder;
  }

  // Get prize from replacementTags
  const tags = await sdk.replacementTags.getAll();
  const prizeTag = tags.find((tag) => tag.name === 'bulk_prize_image')?.value;
  if (prizeTag) {
    const prizeValue = prizeTag.split('"')[1];
    if (prizeValue) {
      prize.value = prizeValue;
      isWinner.value = true;
    }
  }
});

onUnmounted(() => {
  document.body.classList.remove('hide-gameplay');
});
</script>

<template>
  <div class="claw-game">
    <div class="machine-container">
      <img :src="machineFrame" alt="Claw Machine" class="machine-frame" />
      <div class="game-area">
        <div ref="clawContainer" class="claw-container">
          <img :src="clawHolder" alt="ClawDimmer" class="claw-dimmer" />
          <div class="claw-prize-wrapper">
            <img :src="clawImage" alt="Claw" class="claw" />
            <img v-if="prize" :src="prize" alt="Prize" class="prize" />
          </div>
        </div>
      </div>
      <div class="drop-button" :class="{ clicked: hasClicked }" @click="handleDrop">
        <img src="/files/449/uploads/c64ee1df-53d0-415a-8af8-6ecf06ddfdcf.png" alt="Drop" class="drop-button-image" />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.claw-game {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  --game-scale: 1;
}

.machine-container {
  position: relative;
  width: 100%;
  height: min(400px, 60vh);
  aspect-ratio: 4/3;
  max-width: min(533px, calc(60vh * 4 / 3));
  transform: scale(var(--game-scale));
  transform-origin: center center;
}

.machine-frame {
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: contain;
  z-index: 3;
}

.game-area {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  height: 70%;
  z-index: 0;
  overflow: hidden;
  border-radius: 10px;
}

.claw-container {
  position: absolute;
  top: 25px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  animation: moveSideToSide 3s ease-in-out infinite alternate;
}

.claw-dimmer {
  position: relative;
  width: 40px;
  object-fit: contain;
  z-index: 2;
}

.claw-prize-wrapper {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 1;
}

@keyframes moveSideToSide {
  0% {
    transform: translateX(-180%);
  }
  100% {
    transform: translateX(80%);
  }
}

.claw {
  position: absolute;
  top: -150px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: auto;
  z-index: 1;
}

.drop-button {
  position: absolute;
  bottom: 21%;
  left: 51%;
  transform: translateX(-50%);
  width: 40px;
  cursor: pointer;
  z-index: 4;
  transition: transform 0.1s ease;
  animation: glowButton 2s ease-in-out infinite;

  &.clicked {
    animation: none;
    filter: none;
  }

  &:active {
    transform: translateX(-50%) scale(0.95);
  }
}

@keyframes glowButton {
  0% {
    filter: drop-shadow(0 0 0px rgba(255, 0, 0, 0));
  }
  50% {
    filter: drop-shadow(0 -8px 8px rgba(255, 0, 0, 1));
  }
  100% {
    filter: drop-shadow(0 0 0px rgba(255, 0, 0, 0));
  }
}

.drop-button-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.prize {
  position: absolute;
  top: 55px;
  left: 50%;
  transform: translateX(-50%);
  width: 28px;
  height: auto;
  z-index: 0;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.prize.visible {
  opacity: 1;
}

// Desktop styles
:host-context(.site--desktop) {
  .claw-game {
    --game-scale: min(0.9, calc(100vh / 1000px));
  }

  .machine-container {
    height: min(600px, 70vh);
    max-width: min(800px, calc(70vh * 4 / 3));
  }

  .game-area {
    width: 85%;
    height: 75%;
  }

  .claw-container {
    top: 40px;
  }

  .claw-dimmer {
    width: 60px;
  }

  .claw {
    top: -220px;
    width: 60px;
  }

  .drop-button {
    bottom: 21%;
    width: 60px;
  }

  .prize {
    width: 42px;
    top: 86px;
  }
}

// Large screen styles
@media (min-height: 999px) {
  .claw-game {
    --game-scale: min(1.3, calc(100vh / 800px));
  }

  .machine-container {
    height: min(750px, 80vh) !important;
    max-width: min(1067px, calc(80vh * 4 / 3)) !important;
    aspect-ratio: 4/3 !important;
  }

  .game-area {
    width: 90% !important;
    height: 82% !important;
  }

  .claw-container {
    top: 70px !important;
  }

  .claw-dimmer {
    width: 90px !important;
  }

  .claw {
    top: -350px !important;
    width: 90px !important;
  }

  .drop-button {
    bottom: 22% !important;
    width: 90px !important;
  }

  .prize {
    width: 65px !important;
    top: 110px !important;
  }
}

// Large mobile screens
@media (max-width: 768px) and (min-height: 700px) {
  .claw-game {
    --game-scale: min(1.2, calc(100vh / 800px));
  }

  .machine-container {
    height: min(600px, 75vh) !important;
    max-width: min(800px, calc(75vh * 4 / 3)) !important;
    aspect-ratio: 4/3 !important;
  }

  .game-area {
    width: 85% !important;
    height: 75% !important;
  }

  .claw-container {
    top: 50px !important;
  }

  .claw-dimmer {
    width: 70px !important;
  }

  .claw {
    top: -280px !important;
    width: 70px !important;
  }

  .drop-button {
    bottom: 21% !important;
    width: 70px !important;
  }

  .prize {
    width: 50px !important;
    top: 78px !important;
  }
}
</style>
